/**
 * CERTUS API Service
 * Handles all API calls for the CERTUS project management system
 */

import apiService from '../../../services/apiService';

// Format currency for display
export const formatCurrency = (value) => {
  // Handle "not_available" as special case - return "Not Available" string
  if (value === 'not_available' || value === 'Not Available' || value === 'not available') {
    return 'Not Available';
  }
  
  // Handle null, undefined, empty string, or other 'Not Available' variants
  if (value == null || value === '' || value === 'N/A' || value === 'n/a') {
    return null;
  }
  
  // Handle different types: number, string, or object
  let numericValue;
  if (typeof value === 'number') {
    numericValue = value;
  } else if (typeof value === 'string') {
    // Remove dollar signs and commas, then parse
    const cleaned = value.replace(/[$,]/g, '').trim();
    if (cleaned === '' || cleaned === 'N/A' || cleaned === 'n/a' || cleaned === 'not_available' || cleaned === 'not available') {
      return cleaned === 'not_available' || cleaned === 'not available' ? 'Not Available' : null;
    }
    numericValue = parseFloat(cleaned);
  } else if (typeof value === 'object' && value !== null) {
    // Handle DynamoDB Decimal objects
    numericValue = parseFloat(value.toString());
  } else {
    return null;
  }
  
  // Check if it's a valid number (including 0)
  if (isNaN(numericValue)) {
    return null;
  }
  
  // Always format as currency with dollar sign
  return `$${numericValue.toFixed(2)}`;
};

// Format date for display
export const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format date for form input (ISO date string)
export const formatDateForInput = (isoString) => {
  if (!isoString) return new Date().toISOString();
  return isoString;
};

// PROJECT OPERATIONS

export const listProjects = async () => {
  try {
    const response = await apiService.get('/certus/projects');
    // Response might be already parsed by our backend wrapper
    const data = response.data || response;
    return data.projects || [];
  } catch (error) {
    console.error('Error listing projects:', error);
    throw error;
  }
};

export const createProject = async (projectData) => {
  try {
    const response = await apiService.post('/certus/projects', projectData);
    const data = response.data || response;
    return data.project;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const getProject = async (projectId) => {
  try {
    const response = await apiService.get(`/certus/projects/${projectId}`);
    const data = response.data || response;
    return data.project;
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    const response = await apiService.put(`/certus/projects/${projectId}`, projectData);
    const data = response.data || response;
    return data.project;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    const response = await apiService.delete(`/certus/projects/${projectId}`);
    const data = response.data || response;
    return data;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// ADDRESS OPERATIONS

export const listAddresses = async (projectId) => {
  try {
    const response = await apiService.get(`/certus/projects/${projectId}/addresses`);
    const data = response.data || response;
    return data.addresses || [];
  } catch (error) {
    console.error('Error listing addresses:', error);
    throw error;
  }
};

export const createAddress = async (addressData) => {
  try {
    const response = await apiService.post('/certus/addresses', addressData);
    const data = response.data || response;
    return data.address;
  } catch (error) {
    console.error('Error creating address:', error);
    throw error;
  }
};

export const getAddress = async (addressId) => {
  try {
    const response = await apiService.get(`/certus/addresses/${addressId}`);
    const data = response.data || response;
    return data.address;
  } catch (error) {
    console.error('Error getting address:', error);
    throw error;
  }
};

export const updateAddress = async (addressId, addressData) => {
  try {
    const response = await apiService.put(`/certus/addresses/${addressId}`, addressData);
    const data = response.data || response;
    return data.address;
  } catch (error) {
    console.error('Error updating address:', error);
    throw error;
  }
};

export const deleteAddress = async (addressId) => {
  try {
    const response = await apiService.delete(`/certus/addresses/${addressId}`);
    const data = response.data || response;
    return data;
  } catch (error) {
    console.error('Error deleting address:', error);
    throw error;
  }
};

export const getAddressHistory = async (addressId) => {
  try {
    const response = await apiService.get(`/certus/addresses/${addressId}/history`);
    const data = response.data || response;
    return data.history || [];
  } catch (error) {
    console.error('Error getting address history:', error);
    throw error;
  }
};

// IMPORT/EXPORT OPERATIONS

export const parseCSV = async (csvContent, projectId = null, projectName = null) => {
  try {
    const response = await apiService.post('/certus/import/csv', {
      csvContent,
      projectId,
      projectName
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const data = response.data || response;
    return data;
  } catch (error) {
    console.error('Error parsing CSV:', error);
    throw error;
  }
};

export const confirmImport = async (importData) => {
  try {
    const response = await apiService.post('/certus/import/confirm', importData);
    const data = response.data || response;
    return data;
  } catch (error) {
    console.error('Error confirming import:', error);
    throw error;
  }
};

export const exportProjectExcel = async (projectId) => {
  try {
    // Get presigned download URL from backend
    // Note: apiService.get() returns response.data directly, not the full response object
    const data = await apiService.get(`/certus/export/${projectId}`);
    
    console.log('📊 Export data received:', data);
    
    if (data && data.downloadUrl) {
      // Download from S3 presigned URL
      const filename = data.filename || `CERTUS_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      console.log('📥 Downloading from S3:', filename);
      
      // Fetch the file from S3
      const fileResponse = await fetch(data.downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`S3 download failed: ${fileResponse.status} ${fileResponse.statusText}`);
      }
      
      const blob = await fileResponse.blob();
      console.log('✅ Downloaded blob size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } else {
      console.error('❌ Invalid data structure:', data);
      throw new Error('Invalid response from server - no downloadUrl');
    }
  } catch (error) {
    console.error('Error exporting project:', error);
    throw error;
  }
};

// Legacy CSV export (keeping for backwards compatibility)
export const exportProjectCSV = exportProjectExcel;

// JOB PROCESSING OPERATIONS

export const processCompletedTerminixJobs = async (limit = 50) => {
  try {
    const response = await apiService.post('/certus/jobs/process-completed', { limit });
    const data = response.data || response;
    return data;
  } catch (error) {
    console.error('Error processing completed terminix jobs:', error);
    throw error;
  }
};

// FILE UPLOAD HELPER

export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

// Default export with all functions
const certusService = {
  // Projects
  listProjects,
  createProject,
  getProject,
  updateProject,
  deleteProject,
  
  // Addresses
  listAddresses,
  createAddress,
  getAddress,
  updateAddress,
  deleteAddress,
  getAddressHistory,
  
  // Import/Export
  parseCSV,
  confirmImport,
  exportProjectCSV,
  readFileAsText,
  
  // Job Processing
  processCompletedTerminixJobs,
  
  // Batch Scrape
  startBatchScrape: async (projectId, delayMinutes) => {
    try {
      const response = await apiService.post(`/certus/projects/${projectId}/batch-scrape/start`, {
        delayMinutes
      });
      return response;
    } catch (error) {
      console.error('Error starting batch scrape:', error);
      throw error;
    }
  },
  
  cancelBatchScrape: async (batchId) => {
    try {
      const response = await apiService.post(`/certus/batch-scrape/${batchId}/cancel`);
      return response;
    } catch (error) {
      console.error('Error cancelling batch scrape:', error);
      throw error;
    }
  },
  
  getActiveBatchScrape: async (projectId) => {
    try {
      const response = await apiService.get(`/certus/projects/${projectId}/batch-scrape/active`);
      return response.batch || null;
    } catch (error) {
      console.error('Error getting active batch scrape:', error);
      throw error;
    }
  },
  
  // Utilities
  formatCurrency,
  formatDate,
  formatDateForInput
};

export default certusService;


